// English source-of-truth dictionary. Add new keys here first, then mirror in
// si.ts and ta.ts. Keys are grouped by feature area, not by component, so
// shared labels (Cancel, Save, Brand name) live in `common.*`.

export const en = {
  common: {
    saving: 'Saving…',
    deleting: 'Deleting…',
    appName: 'Synapse',
    appNameLong: 'Synapse Clinic Inventory',
    version: 'v0.0.1',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    submit: 'Submit',
    submitting: 'Submitting…',
    delete: 'Delete',
    edit: 'Edit',
    remove: 'Remove',
    add: 'Add',
    close: 'Close',
    back: 'Back',
    backToHome: 'Back to home',
    next: 'Next',
    previous: 'Previous',
    loading: 'Loading…',
    searching: 'Searching…',
    lookingUp: 'Looking up…',
    tryAgain: 'Try again',
    retry: 'Retry',
    optional: 'Optional',
    required: 'Required',
    yes: 'Yes',
    no: 'No',
    or: 'Or',
    pick: 'Pick',
    none: 'None',
    all: 'All',
    signOut: 'Sign out',
    admin: 'Admin',
    staff: 'Staff',
    draft: 'Draft',
    pending: 'Pending',
    actions: 'Actions',
    learnMore: 'Learn more',
    justNow: 'just now',
    minutesAgo: '{{count}}m ago',
    hoursAgo: '{{count}}h ago',
    daysAgo: '{{count}}d ago',
    unit_one: 'unit',
    unit_other: 'units',
    item_one: 'item',
    item_other: 'items',
    line_one: 'line',
    line_other: 'lines',
    batch_one: 'batch',
    batch_other: 'batches',
    onHand: 'on hand',
    expired: 'Expired',
    expiringSoon: 'Expiring soon',
    outOfStock: 'Out of stock',
    inStockN: '{{count}} in stock',
    brandName: 'Brand name',
    genericName: 'Generic name',
    strength: 'Strength',
    quantity: 'Quantity',
    unit: 'Unit',
    batch: 'Batch',
    batchNumber: 'Batch number',
    barcode: 'Barcode',
    expiry: 'Expiry',
    manufacturer: 'Manufacturer',
    storageCondition: 'Storage condition',
    notes: 'Notes',
    note: 'Note',
    reason: 'Reason',
    email: 'Email',
    workEmail: 'Work email',
    password: 'Password',
    firstName: 'First name',
    lastName: 'Last name',
    name: 'Name',
    role: 'Role',
    decreaseQuantity: 'Decrease quantity',
    increaseQuantity: 'Increase quantity',
    unknown: 'Unknown',
    language: 'Language',
    changeLanguage: 'Change language',
    refreshPage: 'Refresh page',
  },

  unit: {
    tablets: 'tablets',
    capsules: 'capsules',
    bottles: 'bottles',
    vials: 'vials',
    ml: 'ml',
    sachets: 'sachets',
    ampoules: 'ampoules',
    drops: 'drops',
    pieces: 'pieces',
    boxes: 'boxes',
    pairs: 'pairs',
    rolls: 'rolls',
    packs: 'packs',
    units: 'units',
    tests: 'tests',
    strips: 'strips',
  },

  scan: {
    title: 'Scan barcode',
    description: "Point the camera at the barcode. We'll look it up automatically.",
    use: 'Use',
    captured: 'Captured',
    typeInstead: 'Type instead',
    useCameraInstead: 'Use camera instead',
    rescan: 'Rescan',
    typePrompt: "Type or paste the barcode and we'll look it up.",
    barcodePlaceholder: '0 0000 00000 0',
    err: {
      permissionDenied: 'Camera access denied. Enter the barcode manually.',
      noCamera: 'No camera on this device. Enter the barcode manually.',
      insecureContext: 'Camera needs a secure (HTTPS) page. Enter the barcode manually.',
      unsupported: "This browser can't access the camera. Enter the barcode manually.",
      security: 'Camera blocked by security policy. Enter the barcode manually.',
      inUse: 'Camera is in use by another app. Close it or use "Type instead".',
      overconstrained: 'No camera matches the requested settings. Try "Type instead".',
    },
  },

  greeting: {
    night: 'Good night',
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
  },

  itemType: {
    medication: 'Medication',
    consumable: 'Consumable',
    medical_device: 'Medical device',
    reagent: 'Reagent',
    other: 'Other',
  },

  // Shared clinical field-type components (Stage 4 library).
  fields: {
    other: 'Other',
    add: 'Add',
    addNotes: 'Add notes…',
    typeToSearchOrAdd: 'Type to search or add…',
    searchMedicineDb: 'Search the medicine database…',
    freeText: 'Free text',
    statusEmpty: 'Empty',
    statusFilled: 'Done',
    statusFinding: 'Finding',
  },

  // Phase 1 patient screens (Stage 5): search, quick-create, summary.
  patients: {
    title: 'Patients',
    subtitle: "Search the clinic's patient records by name, phone, or National ID.",
    searchPlaceholder: 'Search by name, phone, or ID',
    searchLabel: 'Search patients',
    clearSearch: 'Clear search',
    recentPatients: 'Recent patients',
    searchingLabel: 'Searching…',
    resultsCount_one: '{{count}} patient matching “{{query}}”',
    resultsCount_other: '{{count}} patients matching “{{query}}”',
    resultsHeader_one: '{{count}} result · “{{query}}”',
    resultsHeader_other: '{{count}} results · “{{query}}”',
    noResultsTitle: 'No patient found',
    noResultsBody: 'Nothing matches “{{query}}”. Check the spelling, or add them as a new patient.',
    addAsNew: 'Add “{{query}}” as new patient',
    newPatient: 'New patient',
    yrs: 'yrs',
    mrnLabel: 'MRN',
    nicLabel: 'NIC',
    noRecentTitle: 'No patients yet',
    noRecentBody: 'Patients you create or open will show up here for quick access.',
    couldNotLoad: "Couldn't load patients",
    couldNotLoadMsg: "Something went wrong loading patients. Please try again.",
    // Desktop master–detail empty pane + keyboard hints.
    selectPatientTitle: 'Select a patient',
    selectPatientBody:
      'Choose someone from the list to open their chart, review history, and start a consultation.',
    kbdSearch: 'search',
    kbdNavigate: 'navigate',
    kbdMove: 'move',
    kbdOpen: 'open',
    backToPatients: 'Back to patients',
    // Quick-create form.
    quickCreate: {
      title: 'New patient',
      subtitle: 'Create a record with the essentials — the rest is captured in the consultation.',
      fullName: 'Full name',
      fullNameHint: 'Carried over from your search.',
      fullNameRequired: 'Enter the patient’s name.',
      sex: 'Sex',
      sexHint: 'Determines whether the obstetric stage appears later.',
      dateOfBirth: 'Date of birth',
      age: 'Age',
      ageHint: 'Use if the exact date of birth is unknown.',
      ageUnit: 'yrs',
      ageApproxHint: 'Saved as an approximate date of birth (1 January).',
      phone: 'Phone',
      nationalId: 'National ID (NIC)',
      nationalIdPlaceholder: 'e.g. 199512300456',
      nationalIdHint: '9 digits then V, or 12 digits',
      nationalIdInvalid:
        "That doesn't look like an NIC. Enter 9 digits followed by V or X (e.g. 953120456V), or the 12-digit number.",
      phoneInvalid: 'Enter a phone number with at least 7 digits.',
      infoNote:
        'Address, occupation and the rest are confirmed in the consultation — this keeps creating a patient fast.',
      create: 'Create & open patient',
      creating: 'Creating…',
      createFailed: 'Could not create the patient. Please try again.',
      createdButExtrasFailed:
        "Patient created — couldn't save date of birth / NIC. Edit the record to add them.",
      photo: 'Photo',
      createdButPhotoFailed:
        "Patient created — couldn't upload the photo. Open the record to add it.",
    },
    // Patient photo capture / upload.
    photo: {
      takePhoto: 'Take photo',
      capture: 'Capture',
      switchCamera: 'Switch camera',
      choose: 'Choose file',
      remove: 'Remove',
      edit: 'Edit photo',
      view: 'View photo',
      hint: 'Use the camera, or pick from your gallery / files.',
      readFailed: "Couldn't read that image. Try another file.",
      captureFailed: "Couldn't capture the photo. Try again.",
      cameraDenied: 'Camera access was blocked. Allow it, or choose a file instead.',
      cameraUnavailable: 'No camera available. Choose a file instead.',
      cameraUnsupported: 'This device or browser cannot use the camera. Choose a file instead.',
      uploadFailed: "Couldn't upload the photo. Please try again.",
      removeFailed: "Couldn't remove the photo. Please try again.",
    },
    // Sex options (reused by Segmented + summary header).
    sexMale: 'Male',
    sexFemale: 'Female',
    sexOther: 'Other',
    sexUnknown: 'Unknown',
    // Edit-details dialog (reuses the quick-create field labels above).
    editDialog: {
      subtitle: 'Update the details captured when this patient was registered.',
      save: 'Save changes',
      saving: 'Saving…',
      saveFailed: 'Could not save changes. Please try again.',
    },
    // Header band.
    editDetails: 'Edit details',
    startConsultation: 'Start consultation',
    // Summary flags + cards.
    allergyFlag: 'Allergy · {{substance}}',
    bloodFlag: 'Blood {{group}}',
    pregnantFlag: 'Pregnant · {{poa}}',
    noKnownAllergies: 'No known allergies recorded.',
    cardAllergies: 'Allergies',
    cardChronicDiseases: 'Chronic diseases',
    cardCurrentMeds: 'Current medications',
    cardPastMedical: 'Past medical & surgical',
    cardObstetric: 'Obstetric snapshot',
    cardFamilySocial: 'Family & social',
    cardVisitTimeline: 'Visit timeline',
    familyLabel: 'Family',
    socialLabel: 'Social',
    noChronicDiseases: 'No chronic conditions recorded.',
    noCurrentMeds: 'No current medications recorded.',
    noPastHistory: 'No past medical or surgical history recorded.',
    noFamilySocial: 'No family or social history recorded.',
    sinceYear: 'Since {{year}}',
    // Obstetric snapshot.
    obGravidaPara: 'Gravida / Para',
    obTrimester: 'Trimester',
    obLmp: 'LMP',
    obEdd: 'EDD',
    obPoa: 'POA {{poa}}',
    obPoaLabel: 'POA',
    obWeeksDays: '{{weeks}}w {{days}}d',
    obFullTerm: 'EDD 40w',
    obTrimesterValue: '{{n}}',
    // Visit timeline.
    noVisitsYet: 'No visits yet',
    noVisitsBody: 'Consultations for this patient will appear here.',
    statusDraft: 'Draft',
    statusFinalized: 'Finalized',
    visitNoDiagnosis: 'Consultation',
    // Smart relative recency of a visit (see visitRecency.ts).
    visitToday: 'Today',
    visitYesterday: 'Yesterday',
    visitDaysAgo_one: '{{count}} day ago',
    visitDaysAgo_other: '{{count}} days ago',
    visitWeeksAgo_one: '{{count}} week ago',
    visitWeeksAgo_other: '{{count}} weeks ago',
    visitMonthsAgo_one: '{{count}} month ago',
    visitMonthsAgo_other: '{{count}} months ago',
    visitYearsAgo_one: '{{count}} year ago',
    visitYearsAgo_other: '{{count}} years ago',
    couldNotLoadHistory: "Couldn't load clinical history.",
    couldNotLoadVisits: "Couldn't load visits.",
    // Read-only summary of a single visit (the data unique to that consultation).
    visitSummary: {
      cardDispensed: 'Medicines given at the clinic',
      cardOutside: 'Medicines to buy outside',
      cardPlan: 'Plan & management',
      cardMeds: 'Medications this visit',
      cardObstetric: 'Obstetric & gynae',
      gestation: 'Gestation',
      gestationWeeks_one: '{{count}} week',
      gestationWeeks_other: '{{count}} weeks',
      withinDays_one: 'within {{count}}d',
      withinDays_other: 'within {{count}}d',
      followUp_one: 'Return in {{count}} day',
      followUp_other: 'Return in {{count}} days',
      noFollowUp: 'No follow-up',
      continueEditing: 'Continue editing',
    },
  },

  // Phase 2 consultation history-taking form (chrome only in P2-1).
  consult: {
    consultation: 'Consultation',
    stageOfM: 'Stage {{n}} of {{total}}',
    stepperLabel: 'Consultation stages',
    back: 'Back',
    saveDraft: 'Save draft',
    reviewAndSave: 'Review & save',
    review: 'Review',
    jumpToStage: 'Jump to stage',
    nextShort: 'Next · {{stage}}',
    saveConsultation: 'Save consultation',
    notMandatory: 'Nothing here is mandatory — partial saves are fine',
    readOnly: 'Finalized — read only',
    placeholderBody: '{{stage}} — coming in a later update.',
    // Stepper step states.
    stepInProgress: 'In progress',
    stepDone: 'Done',
    stepFinal: 'Final step',
    stepEmpty: 'Empty',
    // Autosave indicator.
    saving: 'Saving…',
    saved: 'Saved',
    savedAt: 'Saved {{time}}',
    saveFailed: "Couldn't save",
    // Conflict banner.
    conflictTitle: 'This draft changed elsewhere',
    conflictBody:
      'Someone updated this consultation in another window. Your edits here are kept — reload to merge the latest version, then continue.',
    reload: 'Reload',
    reloading: 'Reloading…',
    // Loading / error.
    couldNotLoad: "Couldn't load the consultation",
    notFound: 'This consultation could not be found.',
    // Stages.
    stage: {
      presenting: {
        label: 'Presenting complaint & systemic review',
        short: 'Complaint',
        sub: 'Capture the complaint, then review systems. Every system defaults to normal — open only the ones with positive findings.',
      },
      past: {
        label: 'Past medical & surgical history',
        short: 'Past Hx',
        sub: 'Confirm carried-forward conditions. Chronic diseases can attach the drugs taken for them.',
      },
      obstetric: {
        label: 'Gynaecological & obstetric history',
        short: 'Gynae/Obs',
        sub: 'Shown because the patient is female. LMP drives the gestogram, POA and EDD.',
      },
      drugs: {
        label: 'Drug history & allergies',
        short: 'Drugs',
        sub: 'Current medications and known drug allergies.',
      },
      family: {
        label: 'Family history',
        short: 'Family',
        sub: 'Hereditary and familial conditions.',
      },
      social: {
        label: 'Social history',
        short: 'Social',
        sub: 'Smoking, alcohol, occupation and lifestyle.',
      },
      review: {
        label: 'Review & save',
        short: 'Review',
        sub: 'Review everything captured, then finalize the consultation.',
      },
    },
    // Systems-review labels (chrome for the chip pools).
    systems: {
      cardiovascular: 'Cardiovascular',
      respiratory: 'Respiratory',
      gastrointestinal: 'Gastrointestinal',
      genitourinary: 'Genitourinary',
      neurological: 'Neurological',
      musculoskeletal: 'Musculoskeletal',
      dermatological: 'Dermatological',
      endocrine: 'Endocrine',
    },
    // LMP gestogram (pregnancy dating) widget.
    gestogram: {
      emptyHint: 'Enter the LMP to see pregnancy dating.',
      lmp: 'LMP',
      entered: 'entered',
      poa: 'POA today',
      poaValue: '{{weeks}}w {{days}}d',
      edd: 'EDD',
      naegele: "Naegele +280d",
      manual: 'manual',
      trimester: 'Trimester',
      triValue: '{{n}}',
      tri1: '1st trimester',
      tri2: '2nd trimester',
      tri3: '3rd trimester',
      autoBadge: 'auto',
      manualBadge: 'manual',
      pastEdd: 'Past the EDD — confirm the pregnancy is still ongoing.',
      pastTerm: 'Past term — this pregnancy is likely over. Record the outcome or update the LMP.',
      editEdd: 'Edit EDD',
      revertEdd: 'Revert EDD to auto',
      eddLabel: 'EDD',
      editPoa: 'Edit POA weeks',
      revertPoa: 'Revert POA to auto',
      poaWeeksLabel: 'POA (weeks)',
      weekShort: '{{n}}w',
      milestone: {
        tri1End: '1st tri ends',
        anatomy: 'Anatomy scan',
        viability: 'Viability',
        term: 'Term',
        edd: 'EDD',
      },
    },
    // Gynae & obstetric stage.
    obstetric: {
      gynaecology: 'Gynaecology',
      obstetric: 'Obstetric',
      prefilled: 'Confirmed',
      lmp: 'LMP',
      lmpHint: 'Drives the gestogram, POA and EDD below.',
      gestogramLabel: 'Gestogram by LMP',
      contraception: 'Contraception',
      cycleNotes: 'Menstrual pattern, cycle & menarche',
      cycleNotesHint: 'Free text — e.g. regular 28-day cycle, normal flow, menarche 13.',
      cycleNotesPlaceholder: 'Regular 28-day cycle, normal flow, menarche at 13…',
      gynaeNotes: 'Cervical screening & known gynae problems',
      gynaeNotesHint: 'Free text — last screening date and any gynae conditions.',
      gynaeNotesPlaceholder: 'Last cervical smear Mar 2024, no known gynae problems…',
      parity: 'Parity record',
      gravida: 'Gravida',
      para: 'Para',
      increment: 'Increase {{field}}',
      decrement: 'Decrease {{field}}',
      pregnancies: 'Previous pregnancies',
      noPregnancies: 'No pregnancies recorded yet.',
      addPregnancy: 'Add pregnancy',
      autoRecorded: 'auto',
      autoRecordedHint: 'Auto-recorded when this pregnancy passed term — confirm the details or remove it.',
      removePregnancy: 'Remove pregnancy {{n}}',
      year: 'Year',
      mode: 'Mode',
      outcome: 'Outcome',
      complications: 'Complications this pregnancy',
      complicationsHint: 'Recorded with the current pregnancy snapshot.',
    },
    // Systemic review & plan stage.
    presenting: {
      systemicReview: 'Systemic review',
      systemsFlagged: '{{count}} of {{total}} systems flagged',
      systemFindings: 'Complaints',
      findingPlaceholder: 'Search or add a complaint…',
      systemNote: 'Note',
      systemNotePlaceholder: 'Add a note for this system…',
      diagnosis: 'Diagnosis',
      diagnosisHint: 'Search or add the working diagnoses for this visit.',
      diagnosisPlaceholder: 'Search or add a diagnosis…',
      // Plan / management (per-visit).
      followUp: 'Follow-up',
      followUpHint: 'Days until the patient was asked to return.',
      followUpDaysShort: 'days',
      followUpPlaceholder: 'e.g. 7',
      investigations: 'Investigations',
      investigationsHint: 'Tests prescribed, each with an optional time window.',
      investigationPlaceholder: 'Search or add an investigation…',
      addInvestigationNamed: 'Add investigation “{{name}}”',
      removeInvestigation: 'Remove investigation {{name}}',
      noInvestigations: 'No investigations prescribed yet.',
      withinDays: 'Within (days)',
      withinDaysShort: 'within days',
      referrals: 'Referrals',
      referralsHint: 'Search or add referral targets.',
      referralPlaceholder: 'Search or add a referral…',
    },
    // Past medical & surgical stage.
    past: {
      prefilled: 'Confirmed',
      previousIllnesses: 'Previous illnesses',
      previousIllnessesHint: 'Past illnesses and resolved conditions.',
      previousIllnessPlaceholder: 'Search or add a past illness…',
      chronic: 'Chronic / non-communicable diseases',
      chronicHint: 'Select the conditions the patient has.',
      surgeries: 'Major surgeries',
      surgeriesHint: 'Procedure with optional year.',
      procedure: 'Procedure',
      procedurePlaceholder: 'e.g. Appendicectomy',
      year: 'Year',
      addSurgery: 'Add surgery',
      removeSurgery: 'Remove surgery {{name}}',
      noSurgeries: 'No surgeries recorded yet.',
      bloodTransfusion: 'Blood transfusion',
      significantTrauma: 'Significant trauma',
    },
    // Drug history & allergies stage.
    drugs: {
      prefilled: 'Confirmed',
      currentMedication: 'Current medication',
      currentMedicationHint: 'Each entry takes a dose & frequency.',
      addMedication: 'Add medication',
      removeMedication: 'Remove {{drug}}',
      noMedications: 'No current medications recorded yet.',
      dose: 'Dose',
      dosePlaceholder: 'e.g. 500 mg',
      frequency: 'Frequency',
      frequencyPlaceholder: 'e.g. BD',
      adviceCompliance: 'Treatment compliance',
      adviceComplianceHint: "Overall adherence to the doctor's advice and prescribed treatment.",
      complianceGood: 'Good',
      compliancePartial: 'Partial',
      compliancePoor: 'Poor',
      allergies: 'Drug allergies',
      allergiesHint: 'Surfaces as the red allergy flag on the patient summary.',
      allergySubstance: 'Substance',
      allergySubstancePlaceholder: 'Search or add a substance…',
      allergyReaction: 'Reaction',
      allergyReactionPlaceholder: 'e.g. Rash',
      allergySeverity: 'Severity',
      severityMild: 'Mild',
      severityModerate: 'Moderate',
      severitySevere: 'Severe',
      addAllergy: 'Add allergy',
      removeAllergy: 'Remove allergy {{name}}',
      noAllergies: 'No drug allergies recorded.',
    },
    // Family history stage.
    family: {
      prefilled: 'Confirmed',
      hereditary: 'Hereditary diseases',
      hereditaryHint: 'Conditions that run in the family.',
      relatives: 'Similar illnesses in family members',
      relativesHint: 'Note the affected relative and condition — e.g. Mother — DM.',
      relativesPlaceholder: 'e.g. Mother — DM…',
      additions: 'Other additions',
    },
    // Social history stage.
    social: {
      prefilled: 'Confirmed',
      smoking: 'Smoking',
      smokingNever: 'Never',
      smokingEx: 'Ex-smoker',
      smokingCurrent: 'Current',
      alcohol: 'Alcohol use',
      alcoholNever: 'Never',
      alcoholOccasional: 'Occasional',
      alcoholRegular: 'Regular',
      living: 'Living condition',
      livingHint: 'Who the patient lives with.',
      occupation: 'Occupation & housing notes',
      occupationHint: 'Free text — occupation, housing and other social notes.',
      occupationPlaceholder: 'Schoolteacher, lives in own home…',
    },
    // Review & save stage.
    reviewSave: {
      edit: 'Edit',
      editStage: 'Edit {{stage}}',
      emptyOptional: 'Empty · optional',
      systemsFlagged: '{{count}} systems flagged',
      followUpInDays: 'Follow-up in {{count}}d',
      pregnanciesCount: '{{count}} pregnancies',
      allergyPrefix: 'Allergy:',
      whatHappens: 'What happens on save',
      newVisitTitle: 'New visit entry',
      newVisitBody:
        'Complaint, systemic review, current meds & obstetric values are added to the timeline.',
      recordUpdatedTitle: 'Record updated',
      recordUpdatedBody:
        'Allergies, chronic diseases, surgeries & baselines update so the next visit pre-fills.',
      finalizeHint: "Use “Save consultation” below to finalize. Nothing here is mandatory.",
    },
  },

  lab: {
    title: 'Lab worklist',
    subtitle: 'Samples collected and reports outstanding.',
    reportsTitle: 'Lab reports',
    reportsSubtitle: 'Every report received, newest first.',
    accession: 'Accession',
    orderedBy: 'Ordered by',
    external: 'External',
    priority: { routine: 'Routine', urgent: 'Urgent' },
    specimen: { blood: 'Blood', urine: 'Urine', stool: 'Stool', other: 'Other' },
    status: {
      awaiting_collection: 'To collect',
      recollect_required: 'Re-collect',
      collected: 'In lab',
      partially_resulted: 'Part resulted',
      resulted: 'Resulted',
      cancelled: 'Cancelled',
    },
    segment: { toCollect: 'To collect', inLab: 'In lab', resulted: 'Resulted', all: 'All' },
    filterLabel: 'Filter by status',
    laboratory: 'Laboratory',
    laboratoryUnset: 'Not set',
    allLaboratories: 'All laboratories',
    noLaboratory: 'No laboratory set',
    filterLaboratory: 'Filter by laboratory',
    searchLabel: 'Search lab orders',
    searchPlaceholder: 'Patient name or accession',
    showCancelled: 'Show cancelled',
    dateFrom: 'From',
    dateTo: 'To',
    today: 'Today',
    newOrder: 'New lab order',
    testCount_one: '{{count}} test',
    testCount_other: '{{count}} tests',
    capNotice: 'Showing the first {{count}} orders in this range. Narrow the dates to see the rest.',
    emptyToCollect: 'Nothing waiting to be collected.',
    emptyInLab: 'Nothing waiting on the lab.',
    emptyResulted: 'No reports back in this range.',
    emptyAll: 'No lab orders in this range.',
    emptyReports: 'No reports match.',
    loadFailed: "Couldn't load lab orders.",
    reportsLoadFailed: "Couldn't load reports.",
    collectedAt: 'Collected',
    notCollected: 'Not collected',
    rejected: 'Rejected',
    openOrder: 'Open order {{accession}}',
    openReport: 'Open report',
    reportFor: 'Report for {{test}}',
    uploadedBy: 'Uploaded',
    supersededBadge: 'Superseded',
    removedBadge: 'Removed',
    detail: {
      back: 'Back to worklist',
      loadFailed: "Couldn't load this order.",
      notFound: 'That lab order no longer exists.',
      indication: 'Indication',
      changeLaboratory: 'Change laboratory',
      laboratorySaveFailed: "Couldn't change the laboratory.",
      specimens: 'Samples',
      tests: 'Tests',
      reports: 'Reports',
      noSpecimens: 'No sample recorded yet.',
      collect: 'Record sample',
      collectType: 'Record {{type}} sample',
      recollect: 'Re-collect',
      reject: 'Reject sample',
      rejectTitle: 'Reject this {{type}} sample?',
      rejectExplain:
        'The tests on this sample go back to the collection queue. The rejected sample stays on the record.',
      rejectReason: 'Reason',
      rejectNote: 'Note',
      rejectConfirm: 'Reject sample',
      rejectedBecause: 'Rejected — {{reason}}',
      reason: {
        haemolysed: 'Haemolysed',
        insufficient: 'Insufficient volume',
        clotted: 'Clotted',
        mislabelled: 'Mislabelled',
        leaked: 'Leaked',
        contaminated: 'Contaminated',
        other: 'Other',
      },
      cancelOrder: 'Cancel order',
      cancelOrderTitle: 'Cancel this order?',
      cancelOrderExplain: 'The requisition stays on the record with your reason.',
      cancelTest: 'Cancel test',
      cancelTestTitle: 'Cancel {{test}}?',
      cancelReasonLabel: 'Reason',
      cancelledBecause: 'Cancelled — {{reason}}',
      awaitingReport: 'Awaiting report',
      uploadReport: 'Upload report',
      replaceReport: 'Replace report',
      uploading: 'Uploading…',
      uploadCancel: 'Cancel upload',
      chooseFile: 'Choose a PDF',
      dropHint: 'Drop a PDF here, or choose a file.',
      notPdf: "That isn't a PDF. Lab reports must be PDF files.",
      tooLarge: 'That file is over 20 MB. Ask the lab for a smaller scan.',
      emptyFile: 'That file is empty.',
      uploadFailed: "Couldn't upload the report.",
      duplicateWarning: 'This looks like a file already attached to this order.',
      conflictTitle: 'Someone else uploaded a report',
      conflictExplain:
        'A report for this test was uploaded while you were working. Replacing it keeps both on the record.',
      replaceAnyway: 'Replace anyway',
      removeReport: 'Remove report',
      removeReportTitle: 'Remove this report?',
      removeReportExplain:
        'The test goes back to awaiting a report. The file is kept on the record, not destroyed.',
      openFailed: "Couldn't open the report.",
      noReports: 'No reports yet.',
      offlineUpload: 'Uploading a report needs a connection.',
    },
    noReportAccess: 'You do not have access to open reports.',
    config: {
      title: 'Lab config',
      subtitle: "The lab desk's reference data: the tests you offer and the labs you use.",
      tabsLabel: 'Choose a list',
    },
    catalogue: {
      title: 'Test list',
      subtitle: 'The tests this clinic offers. Built-in tests plus your own.',
      addTitle: 'Add a test',
      name: 'Name',
      code: 'Code',
      codeHint: 'Short, and never reused.',
      specimen: 'Sample',
      container: 'Container',
      preset: 'Built-in',
      custom: 'Yours',
      hidden: 'Hidden',
      hide: 'Hide',
      show: 'Show',
      hideTest: 'Hide {{test}}',
      showTest: 'Show {{test}}',
      suggestionsTitle: 'Typed on past orders',
      suggestionsHint: 'Names staff have typed by hand. Add one to the list to stop it being retyped.',
      errors: {
        incomplete: 'A test needs both a name and a code.',
        duplicate: 'That code is already in the list.',
        saveFailed: "Couldn't save that change.",
      },
    },
    laboratories: {
      title: 'Laboratories',
      subtitle: 'The labs this clinic sends work to. Never shown to patients.',
      addTitle: 'Add a laboratory',
      // Not plain "Name": this section sits under the test list, whose add form
      // already has one.
      name: 'Laboratory name',
      rename: 'Rename',
      renameLab: 'Rename {{lab}}',
      hidden: 'Hidden',
      hide: 'Hide',
      show: 'Show',
      hideLab: 'Hide {{lab}}',
      showLab: 'Show {{lab}}',
      empty: 'No laboratories yet.',
      inUseHint:
        'Hiding a laboratory keeps it on past orders; it just stops appearing on new ones.',
      loadFailed: "Couldn't load the laboratories.",
      errors: {
        incomplete: 'A laboratory needs a name.',
        duplicate: 'That laboratory is already in the list.',
        saveFailed: "Couldn't save that change.",
      },
    },
    reports: {
      searchLabel: 'Search reports',
      testFilter: 'Test',
      allTests: 'All tests',
      page: 'Page {{page}}',
      scopedToPatient: 'Showing reports for {{name}}',
      thisPatient: 'this patient',
      clearPatientScope: 'Show reports for all patients',
    },
    patient: {
      title: 'Lab',
      empty: 'No lab orders yet.',
      loadFailed: "Couldn't load this patient's lab orders.",
      seeAll: 'See all reports',
      reportOnFile: 'Report on file',
    },
    new: {
      title: 'New lab order',
      stepPatient: 'Patient',
      stepTests: 'Tests',
      stepCollection: 'Collection',
      next: 'Continue',
      back: 'Back',
      submit: 'Create order',
      saving: 'Creating…',
      submitFailed: "Couldn't create the order.",
      patientChosen: 'Patient',
      changePatient: 'Choose someone else',
      choosePatient: 'Choose a patient',
      patientLoadFailed: "Couldn't load that patient.",
      registerPatient: 'Register a new patient',
      filterTests: 'Find a test',
      filterTestsPlaceholder: 'Name or code',
      noTestsMatch: 'No tests match that.',
      catalogueFailed: "Couldn't load the test list.",
      willDraw: 'Samples needed',
      drawLine_one: '{{type}} — {{container}} ({{count}} test)',
      drawLine_other: '{{type}} — {{container}} ({{count}} tests)',
      anyContainer: 'any container',
      chosenCount_one: '{{count}} test chosen',
      chosenCount_other: '{{count}} tests chosen',
      removeTest: 'Remove {{test}}',
      otherTest: 'Not on the list?',
      otherTestName: 'Test name',
      otherTestPlaceholder: 'e.g. Vitamin D',
      otherTestType: 'Sample',
      samples: 'Samples',
      collectedType: '{{type}} sample collected',
      collectedAt: 'Collected at',
      container: 'Container',
      labelNote: 'Label note',
      uncollectedHint:
        'Leave a sample unticked if it has not been taken yet — the order waits in the collection queue.',
      priority: 'Priority',
      laboratoryHint: 'Which lab this work is sent to. Not shown to the patient.',
      orderedByMe: '{{name}} (you)',
      orderedByExternal: 'Someone outside this clinic',
      externalName: 'Ordering doctor',
      externalPlaceholder: 'Name on the referral',
      indicationPlaceholder: 'Why these tests were ordered',
      errors: {
        noPatient: 'Choose a patient first.',
        noTests: 'Choose at least one test.',
        tooManyTests: 'That is more tests than one order can hold.',
        badTime: 'That collection time is not a valid date and time.',
        futureTime: 'A collection time cannot be in the future.',
        noOrderer: 'Enter the name of the doctor who ordered these tests.',
        freeText: {
          empty: 'Type a test name first.',
          duplicate: 'That test is already on this order.',
          full: 'An order can hold at most {{max}} tests.',
        },
      },
    },
  },

  selectClinic: {
    title: 'Choose a clinic',
    subtitle: 'Signed in as {{name}}',
    yourClinics: 'Your clinics',
    invitations: 'Invitations',
    acceptJoin: 'Accept & join',
    createAnother: 'Create a new dispensary',
    errAcceptFailed: "Couldn't join that clinic. Please try again.",
    disabled: 'Disabled',
    disabledHint: 'Switched off by the platform. Contact your administrator.',
    platformAdmin: 'Platform admin',
  },

  nav: {
    consulting: 'Now seeing',
    recordVisit: 'Record visit',
    lab: 'Lab',
    labWorklist: 'Worklist',
    labReports: 'Reports',
    labConfig: 'Lab config',
    workspace: 'Workspace',
    allClinics: 'All clinics',
    switchClinic: 'Switch clinic',
    views: 'Views',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    navigation: 'Navigation',
    home: 'Home',
    patients: 'Patients',
    aptCounter: 'Apt Counter',
    stock: 'Stock',
    addStock: 'Add stock',
    issue: 'Issue',
    adjust: 'Adjust',
    activity: 'Activity',
    people: 'People',
    auditLog: 'Audit log',
    roles: 'Roles',
    issuingAnalytics: 'Issuing',
    appointments: 'Appointments',
    stocks: 'Stocks',
    admin: 'Admin',
    staffView: 'Staff',
    doctorView: 'Doctor',
    registry: 'Registry',
  },

  registry: {
    title: 'Patient registry',
    selectTitle: 'Select a patient',
    selectBody: 'Choose someone from the list to view their details, or register a new patient.',
    openFullRecord: 'Open full record',
    demographicsOnly: 'Clinical history is available to doctors only.',
    profile: {
      viewPhoto: 'View photo',
      dateOfBirth: 'Date of birth',
      sex: 'Sex',
      phone: 'Phone',
      registered: 'Registered',
    },
  },

  auth: {
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signInFailed: 'Sign-in failed. Please try again.',
    useStaffCredentials: 'Use your staff credentials.',
    newHere: 'New here?',
    createAccount: 'Create an account',
    alreadyHaveAccount: 'Already have an account?',
    createYourAccount: 'Create your account',
    oneLoginCovers: 'One login covers every dispensary you join.',
    creatingAccount: 'Creating account…',
    createAccountFailed: 'Could not create your account. Please try again.',
    atLeast8: 'At least 8 characters',
    atLeast8Error: 'At least 8 characters.',
    // Full sentence with link slots. Rendered via <Trans> so non-Latin
    // languages can reorder the verb without splitting the sentence into
    // separate keys.
    agreeTermsLine: 'I agree to the <terms>Terms</terms> and the clinic data <policy>handling policy</policy>.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    pwWeak: 'Weak',
    pwFair: 'Fair',
    pwGood: 'Good',
    pwStrong: 'Strong',
    pwPolicy10: 'At least 10 characters',
    pwPolicyMix: 'Mix of letters and numbers',
    pwPolicySymbol: 'One symbol (recommended)',
    forgotPasswordLink: 'Forgot password?',
    forgotPassword: 'Forgot your password?',
    forgotPasswordBody: "Enter your work email and we'll send you a link to choose a new password.",
    sendResetLink: 'Send reset link',
    sendingResetLink: 'Sending…',
    resetRequestFailed: 'Could not send a reset link. Please try again.',
    resetLinkSentBody: 'Check your inbox.',
    // Hedged on purpose. The server answers identically whether or not the
    // address belongs to an account, so that it cannot be used to test who works
    // here — which makes this sentence the only place a user who mistyped their
    // address can find out why nothing arrived.
    resetLinkSentDetail:
      'If an account exists for {{email}}, a reset link is on its way. It expires in an hour. Check your spam folder if it does not appear.',
    backToSignIn: 'Back to sign in',
    chooseNewPassword: 'Choose a new password',
    chooseNewPasswordBody: 'Pick something you have not used here before.',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    passwordsDoNotMatch: 'Passwords do not match.',
    resetPassword: 'Reset password',
    resettingPassword: 'Resetting…',
    resetFailed: 'Could not reset your password. Please try again.',
    passwordResetDone: 'Password updated',
    passwordResetDoneBody: 'You can sign in with your new password now.',
    resetLinkUnusableBody: 'This link cannot be used. Ask for a new one and try again.',
    requestNewResetLink: 'Request a new link',
    resetRejection: {
      invalid: 'This reset link is not valid.',
      expired: 'This reset link has expired.',
      used: 'This reset link has already been used.',
    },
    brand: {
      loginHeadingLine1: 'Stock arriving and',
      loginHeadingLine2: 'going out, on the record.',
      loginBody:
        "Receive batches, issue items to patients and correct stock that's gone bad — across every clinic, on any device.",
      registerHeadingLine1: 'One Synapse account.',
      registerHeadingLine2: 'Every dispensary you work at.',
      registerBody:
        'After you set up your account you can create a dispensary for your clinic, or accept an invite to join one that already exists.',
      featureTrackTitle: 'Track every batch',
      featureTrackBody: 'Receipts, issues and adjustments — all auditable.',
      featureRolesTitle: 'Roles built in',
      featureRolesBody: 'Admins manage, staff dispense. Switch between dispensaries.',
      featureCalmTitle: 'Calm under load',
      featureCalmBody: 'Designed for one-handed mobile and fast desk-side work.',
      forgotHeadingLine1: 'Locked out happens.',
      forgotHeadingLine2: 'Getting back in is quick.',
      forgotBody:
        'We will email you a single-use link. It works for an hour, and asking for a new one retires the old.',
    },
  },

  home: {
    quickActions: 'Quick actions',
    atAGlance: 'At a glance',
    recentActivity: 'Recent activity',
    summary: 'Summary',
    addStockDesc: 'Record new arrivals',
    issueDesc: 'Dispense from on-hand stock',
    adjustment: 'Adjustment',
    stockStatus: 'Stock status',
    outOfStock: 'Out of stock',
    expiringSoon: 'Expiring soon',
    items: 'Items',
    onHand: 'On hand',
    alreadyExpired: '{{count}} already expired →',
    couldNotLoadSummary: "Couldn't load summary",
    summaryError: 'Could not load summary.',
    noAccessTitle: 'Nothing assigned to you yet',
    noAccessBody: "Your role doesn't include any of the actions on this page. Ask an administrator at your clinic to adjust it.",
    emptyInventoryTitle: 'Your inventory is empty',
    emptyInventoryBody:
      "Add your first stock to start tracking what's on hand and what's expiring.",
    kindStockIn: 'Stock in',
    kindIssue: 'Issued',
    kindAdjustment: 'Adjustment',
    by: 'by',
  },

  addStock: {
    title: 'Add stock',
    subtitle: 'Record items arriving in a batch. Scan or enter manually.',
    scanBarcode: 'Scan barcode',
    scanHint: 'Fastest way — pre-fills item details',
    orEnterManually: 'Or enter manually',
    orEnterManuallyHint: 'Fill the form below — same outcome.',
    itemType: 'Item type',
    capturedFromScan: 'Captured from scan',
    pastDateHint: 'This date is in the past',
    unlock: 'Unlock',
    unlockBarcode: 'Unlock barcode',
    chooseFromList: 'Choose from list',
    custom: 'Custom…',
    clearForm: 'Clear form',
    updateItem: 'Update item',
    addToBatch: 'Add to batch',
    thisBatch: 'This batch',
    noItemsYet: 'No items yet',
    itemsAndUnits: '{{count}} {{itemLabel}} · {{units}} {{unitLabel}}',
    nothingInBatch: 'Nothing in this batch',
    nothingInBatchBody: 'Scan or fill in an item on the left, then add it here.',
    addAtLeastOne: 'Add at least one item, then submit.',
    submitWillRegister: 'Submit registers all {{count}} {{itemLabel}} as received.',
    submitBatch: 'Submit batch',
    submitting: 'Submitting…',
    newSku: 'new SKU',
    batchReceived: 'Batch received',
    batchReceivedBody: '{{count}} {{itemLabel}} · {{units}} {{unitLabel}} added to your inventory.',
    startAnotherBatch: 'Start another batch',
    errInvalidEntries: 'One or more entries are invalid. Review the list and try again.',
    errSubmitFailed: 'Could not submit batch. Try again in a moment.',
    placeholderBrand: 'e.g. Panadol',
    placeholderGeneric: 'e.g. Paracetamol',
    placeholderStrength: 'e.g. 500 mg',
    placeholderBatchNumber: 'e.g. PCM-2511-B',
    placeholderBarcode: 'Scan or type — e.g. 8901030123456',
    placeholderManufacturer: 'e.g. Roche',
    placeholderStorage: 'e.g. 2–8 °C',
    placeholderNotes: 'Anything else worth recording',
    placeholderCustomUnit: 'e.g. blister',
    matched: 'Matched.',
    barcodeNoMatch:
      "No item matches this barcode. We'll carry it over so you can fill the rest once.",
    nmraTag: 'NMRA',
  },

  issue: {
    title: 'Issue',
    subtitle: 'Release items from stock. Pick or scan, choose a batch, and add to the release.',
    scanBarcode: 'Scan barcode',
    scanHint: 'Shortcut — finds the item and pre-fills the line',
    orSearch: 'Or search',
    orSearchHint: 'Type a brand or generic name below.',
    findItem: 'Find item',
    findItemHint: 'Type at least two letters of the brand or generic name.',
    placeholderSearch: 'e.g. Amoxicillin',
    noItemsMatch: 'No items match.',
    batchLoadingHint: 'Loading batches…',
    batchDefaultHint: 'Defaulting to the oldest expiry. Pick another below if needed.',
    noStockedBatches: 'No stocked batches available.',
    upToBalance: 'up to {{balance}}',
    onlyAvailable: 'Only {{balance}} available in this batch — add another line for the remainder.',
    addToRelease: 'Add to release',
    cancelSelection: 'Cancel selection',
    thisRelease: 'This release',
    noLinesYet: 'No items yet',
    linesAndUnits: '{{count}} {{lineLabel}} · {{units}} {{unitLabel}}',
    nothingInRelease: 'Nothing in this release',
    nothingInReleaseBody: 'Pick or scan an item on the left, choose a batch, and add it here.',
    addAtLeastOne: 'Add at least one line, then submit.',
    submitWillRelease: 'Submit releases all {{count}} {{lineLabel}} at once.',
    releaseBtn: 'Issue',
    releasing: 'Releasing…',
    released: 'Released',
    releasedBody: '{{count}} {{lineLabel}} · {{units}} {{unitLabel}} issued from inventory.',
    startAnotherRelease: 'Start another release',
    errInsufficient:
      '{{message}}. Someone else may have just drained the batch — adjust the affected line and resubmit.',
    errInvalidLines: 'One or more lines are invalid. Review the list and try again.',
    errSubmitFailed: 'Could not submit. Try again in a moment.',
    unknownBarcode: 'Unknown barcode — this item has never been stocked here.',
    // Full sentence — keep item name as an interpolation slot so non-English
    // word orders work.
    noAvailableStockOf: '{{item}} has no available stock right now.',
    noExpiry: 'no expiry',
    batchDash: 'batch —',
    nOnHand: '{{count}} on hand',
  },

  adjust: {
    title: 'Adjust',
    subtitle:
      "Remove stock that's expired, damaged, or otherwise can't be issued. Pick the exact batch and reason.",
    scanBarcode: 'Scan barcode',
    scanHint: 'Shortcut — finds the item and lets you choose the batch',
    orSearch: 'Or search',
    orSearchHint: 'Type a brand or generic name below.',
    expiringHeading: 'Expiring or expired',
    expiringSubtitle: '{{count}} {{batchLabel}} need attention',
    expiredOn: 'expired {{date}}',
    expDate: 'exp {{date}}',
    reasonHint: 'Required — used in reports to separate wastage from consumption.',
    notePlaceholder: 'Optional detail',
    wholeBatch: 'Adjust the whole batch ({{count}} {{unitLabel}})',
    onlyAvailable: 'Only {{balance}} available in this batch.',
    addAdjustment: 'Add adjustment',
    adjustmentsTitle: 'Adjustments to commit',
    noAdjustmentsYet: 'No adjustments yet',
    linesAndUnits: '{{count}} {{lineLabel}} · {{units}} {{unitLabel}}',
    nothingToAdjust: 'Nothing to adjust',
    nothingToAdjustBody: 'Pick an item or batch on the left, choose a reason, and add it here.',
    addAtLeastOne: 'Add at least one line, then review.',
    reviewHint: "You'll see a confirmation before anything is removed.",
    reviewBtn: 'Review & confirm',
    confirmTitle: 'Confirm adjustments',
    confirmBody:
      '{{count}} {{lineLabel}} · {{units}} {{unitLabel}} will be removed from inventory. This cannot be undone.',
    removeLinePlain: 'Remove {{quantity}} {{unitLabel}} of {{brand}}',
    batchLabel: 'Batch {{number}}',
    reasonLabel: 'reason: {{reason}}',
    committing: 'Committing…',
    commitBtn: 'Commit adjustments',
    adjusted: 'Adjusted',
    adjustedBody: '{{count}} {{lineLabel}} · {{units}} {{unitLabel}} removed from inventory.',
    startAnother: 'Start another adjustment',
    errInsufficient:
      '{{message}}. The batch may have been issued in the meantime — review the affected line and retry.',
    errInvalidLines: 'One or more lines are invalid. Cancel, review the list, and try again.',
    errCommitFailed: 'Could not commit. Try again in a moment.',
    noAvailableStockOf: '{{item}} has no stock to adjust.',
    reasonExpired: 'Expired',
    reasonDamaged: 'Damaged',
    reasonDiscarded: 'Discarded',
    reasonOther: 'Other',
    pickBatchHint: 'Pick the exact batch being adjusted.',
    confirmSingleBatchHint: 'Confirm by tapping the batch below.',
  },

  stock: {
    title: 'Stock',
    subtitle: "What's on hand, what's running out, and what's at risk of expiring.",
    filterAll: 'All',
    filterOut: 'Out of stock',
    filterExpiring: 'Expiring soon',
    filterExpiringWithDays: 'Expiring soon ({{days}}d)',
    filterExpired: 'Expired',
    filterByExpiry: 'Filter by expiry',
    filterByType: 'Filter by type',
    allTypes: 'All types',
    search: 'Search items',
    searchPlaceholder: 'Search by brand or generic name',
    noItemsYet: 'No items yet',
    onceStockComes: 'Once stock comes in, items will appear here.',
    noItemsMatch: 'No items match',
    tryDifferent: 'Try a different search or clear the filters.',
    clearFilters: 'Clear filters',
    couldNotLoadStock: "Couldn't load stock",
    couldNotLoadStockMsg: 'Could not load stock. Try again.',
    couldNotLoadBatches: 'Could not load batches.',
    inStock: 'In stock',
    outOfStock: 'Out of stock',
    nExpired: '{{count}} expired',
    nExpiringSoon: '{{count}} expiring soon',
    nextExp: 'next exp {{date}}',
    onHand: 'on hand',
    zeroOnHand: '0 on hand',
    noStockedBatchesItem: 'No stocked batches.',
    bringItBack: 'to bring this item back.',
    addStockLink: 'Add stock',
    noBatchesShow: 'No batches to show.',
    discard: 'Discard',
    hideDepleted: 'Hide depleted batches',
    showDepleted: 'Show {{count}} depleted {{batchLabel}}',
    showingRange: 'Showing {{start}}–{{end}} of {{total}}',
    pageOf: 'Page {{page}} / {{totalPages}}',
    prev: 'Prev',
    next: 'Next',
  },

  account: {
    title: 'Your account',
    subtitle: 'Who you are signed in as, and the password you sign in with.',
    clinics: 'Clinics you belong to',
    currentClinic: 'current',
    changePassword: 'Change password',
    changePasswordBody:
      'You will stay signed in on this device. Other devices keep their sessions until they expire.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordHint: 'At least {{count}} characters.',
    passwordTooShort: 'Use at least {{count}} characters.',
    passwordsDoNotMatch: 'These two do not match.',
    currentPasswordWrong: 'That is not your current password.',
    newPasswordSame: 'Choose a password different from your current one.',
    passwordChanged: 'Password changed.',
    changing: 'Changing…',
    changeFailed: "Couldn't change your password.",
  },
  audit: {
    title: 'Audit log',
    subtitle: 'Every meaningful change in your clinic — who did it, when, and what was touched.',
    actions: {
      stockIn: 'Stock in',
      stockIssue: 'Stock issue',
      stockAdjustment: 'Stock adjustment',
      stockMovementUpdate: 'Movement corrected',
      stockMovementDelete: 'Movement deleted',
      itemCreate: 'Item created',
      batchCreate: 'Batch created',
      userCreate: 'User added',
      userUpdate: 'User updated',
      userRemove: 'User removed',
      invitationCreate: 'Invitation sent',
      invitationRevoke: 'Invitation revoked',
      invitationAccept: 'Invitation accepted',
      aptTicketIssue: 'Queue number given',
      aptTicketCancel: 'Queue number cancelled',
      consultationRecord: 'Visit recorded',
      consultationAmend: 'Visit corrected',
    },
    // Shown as the visible labels above the filter row, so they read as field
    // names ("Person") rather than as instructions ("Filter by actor").
    filterByActor: 'Person',
    filterByAction: 'Action',
    fromDate: 'From date',
    toDate: 'To date',
    allPeople: 'All people',
    allActions: 'All actions',
    noActivityYet: 'No activity yet',
    noActivityBody:
      "Once people start adding stock, issuing, or managing members, you'll see the trail here.",
    noEventsMatch: 'No events match',
    tryWiderRange: 'Try a wider date range or clear the filters.',
    couldNotLoad: "Couldn't load audit log",
    couldNotLoadMsg: 'Could not load audit log. Try again.',
    clearFilters: 'Clear filters',
    item: 'item',
    user: 'user',
    summaryBatchCreated: '{{brand}} (batch {{batchNumber}})',
    filterAll: 'All',
    filterStockIn: 'Stock in',
    filterIssue: 'Issued',
    filterAdjustment: 'Adjustment',
    searchPlaceholder: 'Search items or actors',
    emptyTitle: 'No movements recorded',
    emptyBody: 'Stock activity will appear here as it happens.',
    columnWhen: 'When',
    columnItem: 'Item',
    columnKind: 'Kind',
    columnQuantity: 'Qty',
    columnBatch: 'Batch',
    columnReason: 'Reason',
    columnActor: 'By',
    loadMore: 'Load more',
    loading: 'Loading…',
    by: 'by',
  },

  activity: {
    title: 'Activity',
    subtitle: 'Every stock movement, newest first. Only the most recent movement for a batch can be corrected.',
    filterKind: 'Type',
    allKinds: 'All movements',
    kindStockIn: 'Stock in',
    kindIssue: 'Issued',
    kindAdjustment: 'Adjustment',
    searchPlaceholder: 'Search by medicine name',
    fromDate: 'From',
    toDate: 'To',
    clearFilters: 'Clear filters',
    noBatch: 'No batch number',
    noExpiry: 'No expiry',
    expires: 'Expires {{date}}',
    unknownActor: 'Unknown',
    edit: 'Edit',
    delete: 'Delete',
    lockedHint: 'Locked — a newer movement exists for this batch',
    emptyTitle: 'No stock activity yet',
    emptyBody: 'Adding, issuing or adjusting stock will show up here, one entry at a time.',
    emptyAction: 'Add stock',
    noMatchTitle: 'No movements match',
    noMatchBody: 'Try a wider date range or clear the filters.',
    couldNotLoad: "Couldn't load activity",
    tryAgain: 'Try again',
    editTitle: 'Edit movement',
    editStockInDescription: 'Correct what arrived — the quantity, the batch, or the item itself.',
    editIssueDescription: 'Correct how much left, and why.',
    quantity: 'Quantity',
    note: 'Note',
    notePlaceholder: 'Optional',
    reason: 'Reason',
    batchSection: 'Batch',
    itemSection: 'Item',
    batchNumber: 'Batch number',
    expiryDate: 'Expiry date',
    save: 'Save changes',
    saving: 'Saving…',
    cancel: 'Cancel',
    noChanges: 'Nothing has changed yet.',
    deleteTitle: 'Delete this movement?',
    deleteBody: 'This removes the entry from the ledger and reverses its effect on stock.',
    deleteBatchWarning: 'The batch it created will be removed too — nothing else has drawn from it.',
    confirmDelete: 'Delete',
    deleting: 'Deleting…',
    viewAll: 'View all',
  },

  analytics: {
    title: 'Issuing analytics',
    subtitle: 'What left the dispensary, when, and which medicines it went to.',

    // `measure.*` are the keys the analytics contract itself emits
    // (`MeasureMeta.labelKey`) — the page renders whatever key the payload names
    // rather than picking its own label, so these two must keep their spelling.
    measure: {
      units: 'Units',
      lines: 'Issues',
    },
    measureLabel: 'Measure',

    // The caveat on every `units` figure that spans more than one medicine —
    // see `MixedUnitsNote` in `features/analytics/IssuingPanels.tsx`.
    mixedUnits: {
      label: 'Mixed units',
      note: 'Units are counted in each medicine’s own unit — tablets, millilitres, pieces — so a total across medicines adds up different things. Read it, and the shares taken from it, as indicative rather than exact. Switch the measure to Issues to compare medicines exactly.',
    },

    period: {
      label: 'Period',
      today: 'Today',
      week: 'This week',
      month: 'This month',
      last30: 'Last 30 days',
      last90: 'Last 3 months',
      custom: 'Custom',
      from: 'From date',
      to: 'To date',
    },

    // The window the page resolved to, which a chip or a pair of date fields
    // does not state and can differ from — an over-long custom range is trimmed.
    windowShowing: 'Showing {{from}} – {{to}}',

    granularity: {
      label: 'Group by',
      day: 'Day',
      week: 'Week',
      month: 'Month',
    },

    filters: 'Filters',
    filtersDone: 'Done',
    itemTypes: 'Item types',
    itemTypesAll: 'All item types',
    medicine: 'Medicine',
    medicineSearch: 'Search a medicine…',
    medicineNoMatch: 'No medicines match',
    medicineSelected: 'Showing {{name}} only',
    medicineClear: 'Show all medicines',
    medicineUnknown: 'Selected medicine',

    headline: 'Headline',
    deltaVs_one: 'vs previous day',
    deltaVs_other: 'vs previous {{count}} days',
    deltaFlat: 'No change',

    trend: {
      title: 'Trend',
      aria: '{{measure}} issued by {{granularity}}, stacked by item type, {{from}} to {{to}}',
      empty: 'Nothing was issued in this period.',
      partialNote:
        'Faded columns cover only part of their period, so they are shorter than the rest — not quieter.',
    },

    byType: {
      title: 'By item type',
      aria: 'Issuing by item type',
      legend: 'Item type breakdown',
      empty: 'Nothing was issued in this period.',
      filterTo: 'Show {{type}} only',
      clear: 'Show all item types',
    },

    topItems: {
      title: 'Top medicines',
      aria: 'Top medicines by {{measure}}',
      empty: 'Nothing was issued in this period.',
      select: 'Filter everything to {{name}}',
      shareOfAll: 'Share of all issuing',
    },

    log: {
      title: 'Issue log',
      subtitle: 'Every line behind the figures above.',
      noBatch: 'No batch',
      noExpiry: 'No expiry',
      expires: 'Exp {{date}}',
      empty: 'No issues to list for this window.',
      couldNotLoad: "Couldn't load the issue log",
    },

    empty: {
      neverTitle: 'Nothing has been issued yet',
      neverBody: 'Once stock starts leaving the dispensary, this page fills in.',
      neverAction: 'Record an issue',
      rangeTitle: 'No issuing in this period',
      rangeBody: 'Nothing left the dispensary between {{from}} and {{to}}.',
      rangeAction: 'Widen to the last 3 months',
      medicineTitle: 'No issuing for {{name}}',
      medicineTitleUnknown: 'No issuing for the selected medicine',
      medicineBody: 'It was not issued between {{from}} and {{to}}.',
      medicineAction: 'Clear the medicine filter',
      filteredTitle: 'No issuing matches these filters',
      filteredBody: 'Try a wider period or fewer item types.',
      filteredAction: 'Clear filters',
    },

    couldNotLoad: "Couldn't load issuing analytics",
    couldNotLoadMsg: 'Could not load the figures for this window. Try again.',
  },

  people: {
    title: 'People',
    subtitle: 'Manage members of your clinic. Only admins can see this page.',
    inviteMember: 'Invite member',
    loadingMembers: 'Loading members…',
    couldNotLoad: "Couldn't load members. Try again in a moment.",
    noMembersYet: 'No members yet',
    addFirstTeammate: 'Invite your first teammate to get started.',
    youBadge: 'You',
    joinedOn: 'joined {{date}}',
    cantRemoveSelf: "You can't remove yourself from this clinic",
    membersFooter:
      'Membership is scoped to this clinic. A member you remove keeps their account and everything they recorded here.',
    editMember: 'Edit {{name}}',
    removeMemberTitle: 'Remove {{name}}?',
    removeMemberDesc: 'This removes them from this clinic. Their account stays, along with everything they recorded here.',
    removeMemberBtn: 'Remove member',
    removing: 'Removing…',
    inviteMemberDesc:
      "We'll email them an invitation. They join with the roles you pick, using their own Synapse account — or by creating one if they don't have it yet.",
    invitingBtn: 'Sending…',
    inviteRolesHint: 'They join with these roles. Pick at least one.',
    inviteSentEmailFailed:
      "The invitation is ready, but we couldn't email it. Let {{email}} know to sign in and accept it from their clinic list.",
    emailPlaceholder: 'jane@clinic.com',
    pendingInvitations: 'Pending invitations',
    pendingInvitationsLoading: 'Loading invitations…',
    pendingInvitationsEmpty: 'No invitations waiting to be accepted.',
    pendingInvitationsError: "Couldn't load invitations. Try again in a moment.",
    pendingInvitationsFooter: 'An invitation stays here until it is accepted or revoked.',
    invitedOn: 'invited {{date}}',
    inviteRoleDeleted: 'role deleted',
    revokeInvite: 'Revoke invitation',
    revokeInviteTitle: 'Revoke the invitation to {{email}}?',
    revokeInviteDesc:
      "They won't be able to join with it. You can invite the same address again at any time.",
    revokingInvite: 'Revoking…',
    saving: 'Saving…',
    saveChanges: 'Save changes',
    role: 'Role',
    roles: 'Roles',
    rolesHint: 'Members need at least one role.',
    rolesLockedHint: 'This is the last person who can manage access — give someone else that role first.',
    roleLockedSuffix: '(required)',
    selfRoleHint: 'Be careful changing your own roles — you may lose access.',
    mustHaveManager: 'Someone must be able to manage access',
    noRoles: 'No access',
    errFieldsInvalid: 'One or more fields are invalid.',
    errGeneric: 'Something went wrong. Try again in a moment.',
  },

  dispensary: {
    headerSetup: 'Set up a new dispensary',
    stepXOfY: 'Step {{step}} / {{total}}',
    stepDetails: 'Details',
    stepTeam: 'Team',
    detailsHeading: 'Tell us about it',
    detailsSubtitle: 'A dispensary name and where it lives.',
    teamHeading: 'Invite your team',
    teamSubtitle: 'Optional. You can do this later from People.',
    invitesWaiting: 'Invitations waiting for you',
    roleOnJoin: 'Role on join:',
    joining: 'Joining…',
    acceptJoin: 'Accept & join',
    orCreateBelow: 'Or create your own dispensary below.',
    noInvitesFor:
      "No invitations waiting for {{email}}. If you expect one, ask an admin to invite that email — it'll appear here automatically. Or create your own dispensary below.",
    nameLabel: 'Dispensary name',
    nameHint: 'Shown to your team when switching workspaces.',
    namePlaceholder: 'e.g. Bayview North Dispensary',
    street: 'Street',
    city: 'City',
    region: 'State / region',
    postcode: 'Postcode',
    country: 'Country',
    phone: 'Phone',
    saving: 'Saving…',
    continueBtn: 'Continue',
    firstAdminNotice: "You're the first admin. Invite anyone else now or skip and add later.",
    emailToInvite: 'Email to invite',
    addInviteBtn: 'Add',
    pendingInvites: 'Pending invites · {{count}}',
    willReceiveEmail: 'will receive an invite email',
    skipInviteLater: 'Skip — invite later',
    finishing: 'Finishing…',
    createBtn: 'Create dispensary',
    yourDispensary: 'Your dispensary',
    inProgress: 'New · in progress',
    readyToLaunch: 'New · ready to launch',
    address: 'Address',
    team: 'Team',
    oneAdminYou: '1 admin (you)',
    oneAdminInvites_one: '1 admin (you) · {{count}} invite pending',
    oneAdminInvites_other: '1 admin (you) · {{count}} invites pending',
    settingsNote: 'Anything you skip can be edited later from Settings.',
    errCreateFailed: 'Could not create the dispensary.',
    errEmailListed: 'That email is already in the list.',
    errInvitesFailed: 'Some invites could not be sent: {{details}}',
    errAcceptFailed: 'Could not accept the invitation. Please try again.',
    errUnknown: 'Unknown error',
    errNoRoles: 'This clinic has no roles to invite into.',
  },

  consulting: {
    title: 'Now seeing',
    previous: 'Previous',
    nextPatient: 'Next patient',
    pastVisits: 'Past visits',
    firstVisit: 'This is the first visit on record for this patient.',
    nobodyTitle: 'No patient is registered for number {{number}}',
    nobodyBody: 'The front desk gives a patient a number from the registry. Their details appear here once the number is given.',
    alreadyRecorded: "Today's visit is recorded",
    noCounterAccessTitle: 'This screen follows the appointment counter',
    noCounterAccessBody: 'Your role cannot view the counter. Ask a clinic admin to add that permission.',
  },
  visits: {
    title: 'Record visit',
    subtitle: "Type up the doctor's notes: the diagnosis, the medicines given here, and the medicines to buy outside.",
    waitingTitle: 'Waiting to be recorded',
    waitingEmpty: 'Nobody with a number is waiting to be recorded.',
    recordFor: 'Record visit for number {{number}}, {{name}}',
    otherPatientTitle: 'Another patient',
    recordedTitle: 'Recorded today',
    recordedEmpty: 'No visits recorded today yet.',
    couldNotLoadRecorded: "Could not load today's visits.",
    couldNotLoadVisit: 'Could not load this visit.',
    editFor: 'Edit the visit for {{name}}',
    backToVisits: 'Back to visits',
    visitDate: 'Visit date',
    diagnosis: 'Diagnosis',
    diagnosisHint: 'Type it as the doctor wrote it.',
    diagnosisPlaceholder: 'Diagnosis and notes…',
    diagnosisTooLong: 'Keep this under {{max}} characters.',
    issuedTitle: 'Medicines given from the clinic',
    issuedSubtitle: 'These come out of clinic stock when you save.',
    issuedToAdd: 'To be given',
    addToVisit: 'Add to visit',
    outsideTitle: 'Medicines to buy outside',
    outsideSubtitle: 'Written for the patient to buy elsewhere. Stock is not affected.',
    outsideEmpty: 'None added.',
    outsideAdd: 'Add medicine',
    outsideName: 'Medicine',
    outsideDose: 'Dose',
    outsideFrequency: 'How often',
    outsideDuration: 'For how long',
    outsideNameN: 'Outside medicine {{n}} name',
    outsideDoseN: 'Outside medicine {{n}} dose',
    outsideFrequencyN: 'Outside medicine {{n}} how often',
    outsideDurationN: 'Outside medicine {{n}} for how long',
    outsideRemoveN: 'Remove outside medicine {{n}}',
    finishPicking: 'Add or cancel the selected medicine before saving.',
    needsContent: 'Enter a diagnosis or at least one medicine.',
    saveWillRecord: 'Saving records the visit and updates stock.',
    saveWillAmend: 'Saving updates the visit and adjusts stock.',
    saving: 'Saving…',
    saveVisit: 'Save visit',
    saveChanges: 'Save changes',
    changesSaved: 'Changes saved.',
    recorded: 'Visit recorded',
    recordedBody: 'The visit for {{name}} is saved and stock is updated.',
    nextPatient: 'Next patient',
    editThisVisit: 'Edit this visit',
    lineLocked: 'Stock has moved since — this line can no longer be changed',
    lineWillReturn: 'Will be returned to stock',
    lineKeep: 'Keep',
    lineRemoveFor: 'Remove {{name}} from this visit',
    lineKeepFor: 'Keep {{name}} on this visit',
  },
  aptCounter: {
    title: 'Apt Counter',
    subtitle: 'Manage the queue number and doctor availability. Print the QR for the waiting room.',
    joinClinicFirst: 'Join or create a clinic first.',
    couldNotLoad: 'Could not load the counter.',
    decrement: 'Decrement',
    increment: 'Increment',
    setCounter: 'Set counter number',
    setBtn: 'Set',
    doctorIn: 'Doctor is in',
    doctorOut: 'Doctor is out',
    editDoctorName: 'Edit doctor name',
    markDoctorIn: 'Mark doctor in',
    updateDoctorName: 'Update doctor name',
    doctorOnDuty: 'Doctor on duty',
    doctorDialogDesc: 'The patient display will show this name while the doctor is in.',
    doctorNamePlaceholder: 'Dr. ...',
    doctorName: 'Doctor name',
    qrTitle: 'Patient display QR',
    qrAriaLabel: 'QR code for the patient display',
    nowServing: 'Now serving',
    waiting: 'Waiting',
    next: 'Next',
    callNext: 'Call next',
    skip: 'Skip',
    reset: 'Reset',
    publicView: 'Open public view',
    queueEmpty: 'Queue is empty.',
    addToQueue: 'Add to queue',
    confirmReset: 'Reset the counter to zero?',
    confirmResetBody: 'This clears the current queue and starts over.',
    counter: 'Counter',
    todaysQueue: "Today's queue",
    waitingCount: '{{count}} waiting',
    couldNotLoadQueue: "Could not load today's queue.",
    giveNumber: 'Give number',
    givingNumber: 'Giving number…',
    queueNumber: 'Number',
    numberToday: 'Number {{number}} today',
    numberLabel: 'No. {{number}}',
    statusCancelled: 'Cancelled',
    statusRecorded: 'Recorded',
    keepNumber: 'Keep',
    confirmCancelNumber: 'Cancel no. {{number}}',
    cancelNumberFor: 'Cancel number {{number}} for {{name}}',
    connecting: 'Connecting…',
    connected: 'Live',
    offline: 'Offline',
  },

  publicCounter: {
    nowServing: 'Now serving',
    doctorOut: 'Doctor is out',
    displayUnavailable: 'Display unavailable',
    linkInvalid: 'This counter link is no longer valid.',
    reconnecting: 'Reconnecting…',
  },

  // Shared by every QR the app renders (waiting-room counter, self-registration).
  qr: {
    generateFailed: 'Could not generate QR code',
    downloadPng: 'Download PNG',
    copyLink: 'Copy link',
    copied: 'Link copied',
    copyFailed: 'Could not copy — copy the link shown above by hand.',
    downloadFailed: 'Could not create the image. Try again, or screenshot the code above.',
  },

  // The public self-registration page patients reach by scanning the clinic's QR.
  publicRegister: {
    heading: 'Register as a patient',
    at: 'at {{clinic}}',
    intro: 'Fill in your details and show this screen at reception.',
    submit: 'Submit registration',
    submitting: 'Submitting…',
    nameRequired: 'Please enter your full name.',
    submitFailed: 'We could not submit your registration. Please try again.',
    rateLimited: 'Too many attempts. Please wait a moment and try again.',
    loading: 'Loading…',
    linkInvalid: 'This registration link is not valid.',
    linkInvalidBody: 'Please ask the clinic for a current link or register at the counter.',
    doneTitle: 'Thank you',
    doneBody: 'Your details have been sent to the clinic. Please see reception to complete your registration.',
    privacyNote: 'Your details are sent only to this clinic and are reviewed by their staff before your record is created.',
  },

  // The patient's own public page (/p/patient/:token), plus the staff-side
  // action that hands the link over.
  patientPortal: {
    loading: 'Loading…',
    mrnLabel: 'Patient no.',
    labsTitle: 'Lab results',
    noLabs: 'No lab tests have been recorded for you yet.',
    orderedOn: 'Ordered',
    collectedOn: 'Sample taken',
    notCollectedYet: 'Sample not taken yet.',
    awaitingResult: 'Result not ready',
    viewReport: 'View report',
    reportOpening: 'Opening…',
    reportFor: 'View the report for {{test}}',
    reportFailed: 'We could not open that report. Please try again.',
    clinicalNote: 'Results are for your records. Please discuss them with your doctor before acting on anything you read here.',
    notFoundTitle: 'This link is not valid.',
    notFoundBody: 'Please ask the clinic for a current link.',
    // Staff side.
    linkButton: 'Patient link',
    linkModalTitle: 'Share results with this patient',
    linkModalDesc: 'The patient can open this link to see their own lab results.',
    linkCardTitle: 'Your results',
    linkAriaLabel: 'QR code for this patient’s results page',
    linkWarning: 'Anyone with this link can see this patient’s lab results. Give it only to the patient.',
  },

  // Staff-side review of patient self-registrations.
  selfRegistrations: {
    qrButton: 'Registration QR',
    qrModalTitle: 'Patient self-registration',
    qrModalDesc: 'Print or display this code. Patients who scan it can enter their own details, which appear here for review.',
    qrCardTitle: 'Register here',
    qrAriaLabel: 'QR code for patient self-registration',
    qrLanguage: 'Form language',
    qrLanguageHint: 'Patients who scan this code open the form in this language. They can still change it.',
    pending: 'Pending',
    pendingTitle: 'Pending registrations',
    pendingEmpty: 'No registrations are waiting for review.',
    pendingSubtitle: 'Submitted by patients. Check the details, then register or dismiss.',
    submittedAgo: 'Submitted {{when}}',
    review: 'Review',
    reviewTitle: 'Review registration',
    reviewSubtitle: 'Check what the patient entered, correct anything wrong, then register them.',
    approve: 'Approve & register',
    approving: 'Registering…',
    reject: 'Dismiss',
    rejecting: 'Dismissing…',
    rejectConfirm: 'Dismiss this registration?',
    rejectConfirmBody: 'The submission is kept for the record but no patient will be created.',
    nationalIdUnreadable: "The submitted ID number could not be read. Please re-enter it or clear the field.",
    approveFailed: 'Could not register this patient.',
    rejectFailed: 'Could not dismiss this registration.',
    approvedButPhotoFailed: 'The patient was registered, but the photo could not be saved.',
    couldNotLoad: 'Could not load registrations.',
    backToQueue: 'Back to registrations',
    openPatient: 'Open the patient record',
    showingSomeOf: 'Showing the {{shown}} oldest of {{total}} waiting. Review these to see the rest.',
  },

  errors: {
    VALIDATION_ERROR: 'Some of the information was missing or invalid. Please check and try again.',
    UNAUTHORIZED: 'Your session has ended. Please sign in again.',
    FORBIDDEN: "You don't have permission to do that.",
    NOT_FOUND: "We couldn't find what you were looking for.",
    CONFLICT: 'That action conflicts with the current state. Refresh and try again.',
    INSUFFICIENT_STOCK: 'There isn’t enough stock to complete this action.',
    RATE_LIMITED: 'Too many attempts. Please wait a moment and try again.',
    INTERNAL_ERROR: 'Something went wrong on our side. Please try again.',
    INTERNAL_CONSISTENCY: 'Something went wrong on our side. Please try again.',
    PARSE_ERROR: 'The server returned an unexpected response.',
    NETWORK_ERROR: 'We couldn’t reach the server. Check your connection and try again.',
    NOT_A_MEMBER: "You don't have access to this clinic.",
    CLINIC_DISABLED: 'This clinic has been switched off. Contact your administrator.',
    fallback: 'Something went wrong. Please try again.',
  },

  platformAdmin: {
    title: 'Platform admin',
    backToClinics: 'Back to clinics',
    loading: 'Loading…',
    tabs: {
      clinics: 'Clinics',
      passwords: 'Passwords',
      superAdmins: 'Super admins',
    },
    users: {
      minChars: 'Type at least {{count}} characters to search.',
      noMatches: 'No one matches that.',
      searchFailed: "Couldn't search for that.",
    },
    clinicDetail: {
      back: 'All clinics',
      created: 'created {{date}}',
      joined: 'joined {{date}}',
      membersTitle: 'Members',
      noMembers: 'Nobody has joined this clinic yet.',
      membersLoadFailed: "Couldn't load this clinic's members.",
      loadFailed: "Couldn't load this clinic.",
    },
    clinics: {
      title: 'Clinics',
      subtitle: 'Every clinic on the platform. Switch one off to lock it completely.',
      searchPlaceholder: 'Search by name or URL',
      memberCount_one: '{{count}} member',
      memberCount_other: '{{count}} members',
      disabledBadge: 'Disabled',
      disable: 'Disable',
      enable: 'Enable',
      empty: 'No clinics match.',
      loadFailed: "Couldn't load the clinics.",
      toggleFailed: "Couldn't change that clinic.",
      confirmTitle: 'Disable {{name}}?',
      confirmBody_one:
        'Its {{count}} member will be locked out immediately, and its QR pages and patient links will stop working. You can switch it back on at any time.',
      confirmBody_other:
        'Its {{count}} members will be locked out immediately, and its QR pages and patient links will stop working. You can switch it back on at any time.',
    },
    passwords: {
      title: 'Reset a password',
      subtitle:
        'Set a password directly for someone who cannot use the emailed reset link. No email is sent.',
      searchLabel: 'Find a user',
      searchPlaceholder: 'Search by email or name',
      setPassword: 'Set password',
      dialogTitle: 'Set a new password for {{name}}',
      dialogDesc: 'This replaces the password on {{email}} immediately.',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      tooShort: 'Use at least {{count}} characters.',
      mismatch: 'The two passwords do not match.',
      relayHint:
        'Nothing is emailed. Pass the new password to them yourself, and ask them to change it once they are back in.',
      done: 'Password changed for {{email}}.',
      failed: "Couldn't change that password.",
    },
    superAdmins: {
      title: 'Super admins',
      subtitle: 'Who can reach this dashboard.',
      add: 'Add super admin',
      addTitle: 'Add a super admin',
      addDesc:
        'Find an existing account and promote it. If they have no account yet, ask them to register first — this never creates one, so nobody has to be handed a password.',
      searchLabel: 'Find a user',
      searchPlaceholder: 'Search by email or name',
      promote: 'Promote',
      promoteFailed: "Couldn't promote that user.",
      alreadyOne: 'Already a super admin',
      demote: 'Demote',
      demoteTitle: 'Demote {{name}}?',
      demoteDesc: '{{email}} will lose access to this dashboard on their next request.',
      demoteFailed: "Couldn't demote that user.",
      lastOneHint: 'At least one super admin must remain.',
      loadFailed: "Couldn't load the super admins.",
    },
  },

  roles: {
    title: 'Roles',
    subtitle: 'Define what each role in your clinic can do.',
    newRole: 'New role',
    loading: 'Loading roles…',
    loadFailed: 'Couldn\'t load roles. Try again in a moment.',
    empty: 'No roles yet',
    managesAccess: 'Manages access',
    cannotGrantHint: 'You can’t grant a permission you don’t have yourself.',
    noDescription: 'No description',
    permissionCount: '{{count}} permissions',
    memberCount: '{{count}} members',
    lastManagerRoleHint: 'This is the only role that can manage access — create another before deleting it.',
    footer: 'Roles are specific to this clinic. Everyone needs at least one.',
    editRole: 'Edit {{name}}',
    dialogDesc: 'Pick everything holders of this role should be able to do.',
    name: 'Role name',
    namePlaceholder: 'Dispenser',
    description: 'Description',
    descriptionPlaceholder: 'What this role is for',
    permissions: 'Permissions',
    saveFailed: 'Couldn\'t save the role.',
    deleteRole: 'Delete {{name}}?',
    deleteConfirm: 'This role has no members. Deleting it cannot be undone.',
    deleteWarning: '{{count}} members hold this role and will lose the access it grants.',
    deleteFailed: 'Couldn\'t delete the role.',
  },

  permissions: {
    groups: {
      patients: 'Patients',
      clinical: 'Clinical record',
      inventory: 'Inventory',
      lab: 'Laboratory',
      frontDesk: 'Front desk',
      insights: 'Insights & oversight',
      clinic: 'Clinic settings',
      people: 'People & access',
    },
    keys: {
      patients_read_label: 'View patients',
      patients_read_description: 'See the patient register and open a patient\'s details.',
      patients_create_label: 'Register patients',
      patients_create_description: 'Add new patients to the register.',
      patients_update_label: 'Edit patient details',
      patients_update_description: 'Correct a patient\'s demographic details.',
      patients_delete_label: 'Delete patients',
      patients_delete_description: 'Remove a patient from the register.',
      patients_photo_manage_label: 'Manage patient photos',
      patients_photo_manage_description: 'Capture, replace or remove a patient\'s photo.',
      clinical_consultations_read_label: 'View consultations',
      clinical_consultations_read_description: 'Read consultation notes and past visits.',
      clinical_consultations_write_label: 'Write consultations',
      clinical_consultations_write_description: 'Start and edit consultation notes.',
      clinical_consultations_finalize_label: 'Finalise consultations',
      clinical_consultations_finalize_description: 'Close a consultation against further edits.',
      clinical_history_read_label: 'View clinical history',
      clinical_history_read_description: 'Read a patient\'s ongoing clinical history.',
      clinical_history_write_label: 'Edit clinical history',
      clinical_history_write_description: 'Update a patient\'s ongoing clinical history.',
      visits_record_label: 'Record visits',
      visits_record_description: "Type up a visit from the doctor's notes, dispense its medicines, and correct it afterwards.",
      inventory_items_read_label: 'View stock catalogue',
      inventory_items_read_description: 'See items, batches and what is expiring.',
      inventory_items_create_label: 'Add new items',
      inventory_items_create_description: 'Create new items in the stock catalogue.',
      inventory_stock_in_label: 'Receive stock',
      inventory_stock_in_description: 'Record stock arriving at the clinic.',
      inventory_stock_issue_label: 'Issue stock',
      inventory_stock_issue_description: 'Dispense stock to patients.',
      inventory_stock_adjust_label: 'Adjust stock',
      inventory_stock_adjust_description: 'Correct quantities for loss, damage or expiry.',
      inventory_movements_read_label: 'View stock activity',
      inventory_movements_read_description: 'See the full ledger of stock movements.',
      inventory_movements_correct_label: 'Correct stock activity',
      inventory_movements_correct_description: 'Edit or delete a recorded stock movement.',
      dashboard_read_label: 'View dashboard',
      dashboard_read_description: 'See the home page stock summary.',
      dashboard_activity_read_label: 'View recent activity',
      dashboard_activity_read_description: 'See who recorded recent stock movements.',
      apt_counter_read_label: 'View appointment counter',
      apt_counter_read_description: 'See the current queue number.',
      apt_counter_manage_label: 'Drive appointment counter',
      apt_counter_manage_description: 'Advance, reset and set the doctor on duty.',
      self_registrations_read_label: 'View self-registrations',
      self_registrations_read_description: 'See patients who registered themselves by QR.',
      self_registrations_review_label: 'Approve self-registrations',
      self_registrations_review_description: 'Approve or reject self-registration submissions.',
      suggestions_read_label: 'Use saved suggestions',
      suggestions_read_description: 'Read the clinic\'s saved free-text picklists.',
      lab_read_label: 'View lab orders',
      lab_read_description: 'See the lab worklist, an order\'s details and the report list.',
      lab_orders_create_label: 'Create lab orders',
      lab_orders_create_description: 'Raise a new requisition for a patient.',
      lab_orders_update_label: 'Edit lab orders',
      lab_orders_update_description: 'Change or cancel an order, or cancel a test on one.',
      lab_specimens_collect_label: 'Record specimens',
      lab_specimens_collect_description: 'Record a sample as collected, or reject one for recollection.',
      lab_results_upload_label: 'Upload lab reports',
      lab_results_upload_description: 'Attach or replace a test\'s report PDF.',
      lab_results_read_label: 'Open lab reports',
      lab_results_read_description: 'Open and download the report PDFs themselves.',
      lab_results_delete_label: 'Delete lab reports',
      lab_results_delete_description: 'Remove a report uploaded in error.',
      lab_catalogue_manage_label: 'Manage lab test list',
      lab_catalogue_manage_description: 'Add, rename or hide the tests this clinic offers.',
      analytics_read_label: 'View issuing analytics',
      analytics_read_description: 'See what was dispensed, by whom and when.',
      audit_read_label: 'View audit log',
      audit_read_description: 'See the full record of who changed what.',
      clinic_update_label: 'Edit clinic details',
      clinic_update_description:
        'Rename the clinic and change its web address. Changing the address breaks existing links and printed QR codes.',
      members_read_label: 'View members',
      members_read_description: 'See the list of people in this clinic.',
      members_create_label: 'Add members',
      members_create_description: 'Add people to this clinic.',
      members_update_label: 'Edit member profiles',
      members_update_description: 'Change a member\'s name or reset their password.',
      members_remove_label: 'Remove members',
      members_remove_description: 'Remove someone from this clinic.',
      invitations_read_label: 'View invitations',
      invitations_read_description: 'See outstanding invitations.',
      invitations_create_label: 'Send invitations',
      invitations_create_description: 'Invite someone to join by email.',
      invitations_revoke_label: 'Revoke invitations',
      invitations_revoke_description: 'Cancel an invitation before it is claimed.',
      roles_read_label: 'View roles',
      roles_read_description: 'See the clinic\'s roles and what each one allows.',
      roles_manage_label: 'Manage access',
      roles_manage_description: 'Create and edit roles, and decide who holds them. Grants control over everyone\'s access.',
    },
  },

};

export type Dict = typeof en;
